import * as React from "react";
import { createPortal } from "react-dom";
import type { PopoverProps } from "../types";
import "./Popover.css";

// =============================================================================
// Popover Component
// Dropdown container with click-outside detection and portal rendering
// =============================================================================

export const Popover = React.memo(function Popover({
  open,
  onClose,
  anchorEl,
  children,
  className,
  style,
}: PopoverProps) {
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const [position, setPosition] = React.useState({ top: 0, left: 0 });

  // ---------------------------------------------------------------------------
  // Position Calculation
  // ---------------------------------------------------------------------------

  React.useLayoutEffect(() => {
    if (!open || !anchorEl) return;

    const updatePosition = () => {
      const rect = anchorEl.getBoundingClientRect();
      const popoverHeight = popoverRef.current?.offsetHeight || 0;
      const viewportHeight = window.innerHeight;

      // Check if there's space below
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      let top: number;
      if (spaceBelow >= popoverHeight || spaceBelow >= spaceAbove) {
        // Position below
        top = rect.bottom + 4;
      } else {
        // Position above
        top = rect.top - popoverHeight - 4;
      }

      setPosition({
        top: Math.max(4, top),
        left: Math.max(4, rect.left),
      });
    };

    updatePosition();

    // Update position on scroll/resize
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, anchorEl]);

  // ---------------------------------------------------------------------------
  // Click Outside Detection
  // ---------------------------------------------------------------------------

  React.useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // Ignore clicks inside popover
      if (popoverRef.current?.contains(target)) return;

      // For clicks within anchor, only ignore if on interactive elements (input, button)
      if (anchorEl?.contains(target)) {
        const isInteractive = target.closest("input, button, [role='button']");
        if (isInteractive) return;
      }

      onClose();
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, anchorEl, onClose]);

  // ---------------------------------------------------------------------------
  // Escape Key Detection
  // ---------------------------------------------------------------------------

  React.useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (!open || !anchorEl) {
    return null;
  }

  const popoverStyle: React.CSSProperties = {
    position: "fixed",
    top: position.top,
    left: position.left,
    ...style,
  };

  return createPortal(
    <div
      ref={popoverRef}
      className={`popover ${className || ""}`}
      style={popoverStyle}
    >
      {children}
    </div>,
    document.body
  );
});
